import { useEffect, useMemo, useState } from '@wordpress/element';
import { resolveCoachAvatarSrc } from '../../utils/resolveCoachAvatarSrc.js';
import CoachStream from './CoachStream.jsx';

function asArray(value) {
  return Array.isArray(value) ? value : [];
}

function asObject(value) {
  return value && typeof value === 'object' && !Array.isArray(value) ? value : {};
}

function asString(value) {
  return typeof value === 'string' ? value : '';
}

function buildSyntheticPreviewEntries(coach) {
  const persona = asObject(coach?.persona);
  const name = asString(persona?.name).trim() || asString(coach?.label).trim() || 'Coach';
  const introduction = asString(persona?.introduction).trim();
  const slogan = asString(persona?.slogan).trim();

  const body = introduction || slogan || `${name} is ready to guide you.`;

  return [
    {
      id: `coach-preview-${asString(coach?.id).trim() || 'coach'}`,
      role: 'coach',
      persona,
      blocks: [
        {
          id: `coach-preview-block-${asString(coach?.id).trim() || 'coach'}`,
          format: 'plain',
          body,
        },
      ],
    },
  ];
}

function getPreviewEntries(coach) {
  const previewEntries = asArray(coach?.preview_entries);
  if (previewEntries.length) {
    return previewEntries;
  }

  const preview = asObject(coach?.preview);
  const previewBlocks = asArray(preview?.blocks);

  if (previewBlocks.length) {
    return [
      {
        id: `coach-preview-${asString(coach?.id).trim() || 'coach'}`,
        role: 'coach',
        title: asString(preview?.title).trim(),
        persona: asObject(coach?.persona),
        blocks: previewBlocks,
      },
    ];
  }

  return buildSyntheticPreviewEntries(coach);
}

function buildTypingPreset(personaTyping, selectedTypingMode) {
  const typing = asObject(personaTyping);
  const baseSpeed = asObject(typing?.speed);

  const baseMin = Number(baseSpeed?.min);
  const baseMax = Number(baseSpeed?.max);

  const safeMin = Number.isFinite(baseMin) ? baseMin : 28;
  const safeMax = Number.isFinite(baseMax) ? baseMax : 42;

  if (selectedTypingMode === 'instant') {
    return {
      speed: {
        min: 0,
        max: 0,
      },
      initialDelay: 0,
      thinkChance: 0,
      thinkExtraMin: 0,
      thinkExtraMax: 0,
      typoChance: 0,
    };
  }

  if (selectedTypingMode === 'fast') {
    return {
      speed: {
        min: 10,
        max: 15,
      },
      initialDelay: 0,
      thinkChance: 0,
      thinkExtraMin: 0,
      thinkExtraMax: 0,
      typoChance: 0,
    };
  }

  return {
    speed: {
      min: safeMin,
      max: safeMax,
    },
    initialDelay: 120,
    thinkChance: Number.isFinite(Number(typing?.thinkChance)) ? Number(typing.thinkChance) : 0.03,
    thinkExtraMin: Number.isFinite(Number(typing?.thinkExtraMin))
      ? Number(typing?.thinkExtraMin)
      : 400,
    thinkExtraMax: Number.isFinite(Number(typing?.thinkExtraMax))
      ? Number(typing?.thinkExtraMax)
      : 900,
    typoChance: Number.isFinite(Number(typing?.typoChance)) ? Number(typing?.typoChance) : 0.008,
  };
}

export default function CoachSettings({
  coachOptions = [],
  selectedCoachId = 'system',
  handleCoachChange = () => {},
  isBusy = false,
  selectedTypingMode = 'normal',
  handleTypingModeChange = () => {},
}) {
  const safeCoachOptions = useMemo(
    () =>
      asArray(coachOptions).filter((coach) => {
        const id = asString(coach?.id).trim();
        return Boolean(id);
      }),
    [coachOptions]
  );

  const initialIndex = useMemo(() => {
    const foundIndex = safeCoachOptions.findIndex((coach) => coach?.id === selectedCoachId);
    return foundIndex >= 0 ? foundIndex : 0;
  }, [safeCoachOptions, selectedCoachId]);

  const [activeIndex, setActiveIndex] = useState(initialIndex);
  const [previewRunNonce, setPreviewRunNonce] = useState(0);

  useEffect(() => {
    setActiveIndex(initialIndex);
  }, [initialIndex]);

  const selectedCoach = safeCoachOptions[activeIndex] || null;
  const persona = asObject(selectedCoach?.persona);
  const previewEntries = useMemo(() => getPreviewEntries(selectedCoach), [selectedCoach]);
  const avatarSrc = resolveCoachAvatarSrc(persona?.avatar?.src);

  const previewSpeed = useMemo(
    () => buildTypingPreset(persona?.typing, selectedTypingMode),
    [persona, selectedTypingMode]
  );

  const coachId = asString(selectedCoach?.id).trim() || 'coach';
  const previewRunKey = `${coachId}::${selectedTypingMode}::${previewRunNonce}`;
  const isInstant = selectedTypingMode === 'instant';

  const coachName =
    asString(persona?.name).trim() || asString(selectedCoach?.label).trim() || 'Coach';
  const coachSlogan = asString(persona?.slogan).trim();
  const avatarText = coachName ? coachName.charAt(0).toUpperCase() : 'C';

  const canGoPrev = safeCoachOptions.length > 1;
  const canGoNext = safeCoachOptions.length > 1;

  const goToIndex = (nextIndex) => {
    const coach = safeCoachOptions[nextIndex];
    if (!coach) return;

    setActiveIndex(nextIndex);
    setPreviewRunNonce((prev) => prev + 1);

    handleCoachChange({
      target: {
        value: asString(coach?.id).trim(),
      },
    });
  };

  const handlePrev = () => {
    if (!safeCoachOptions.length) return;
    const nextIndex = activeIndex <= 0 ? safeCoachOptions.length - 1 : activeIndex - 1;
    goToIndex(nextIndex);
  };

  const handleNext = () => {
    if (!safeCoachOptions.length) return;
    const nextIndex = activeIndex >= safeCoachOptions.length - 1 ? 0 : activeIndex + 1;
    goToIndex(nextIndex);
  };

  const onTypingModeClick = (value) => {
    setPreviewRunNonce((prev) => prev + 1);

    handleTypingModeChange({
      target: {
        value,
      },
    });
  };

  return (
    <section className="dv-br-coachChooser" aria-label="Coach settings">
      <h3 className="dv-br-coachChooser__title">Select Your Coach</h3>
      <p className="dv-pilot-info">For pilot test: please use Lumi</p>

      <p className="dv-br-coachChooser__intro">
        Pick the guide that fits you best. Switch coach or typing speed and the preview updates
        immediately.
      </p>

      <div className="dv-br-coachChooser__stage">
        <div className="dv-br-coachChooser__nav">
          <button
            type="button"
            className="dv-br-coachChooser__arrow"
            onClick={handlePrev}
            disabled={isBusy || !canGoPrev}
            aria-label="Previous coach"
          >
            ◀
          </button>

          <div className="dv-br-coachChooser__counter">
            {safeCoachOptions.length
              ? `Coach ${activeIndex + 1} of ${safeCoachOptions.length}`
              : '0 coaches found'}
          </div>

          <button
            type="button"
            className="dv-br-coachChooser__arrow"
            onClick={handleNext}
            disabled={isBusy || !canGoNext}
            aria-label="Next coach"
          >
            ▶
          </button>
        </div>

        <div className="dv-br-coachChooser__preview">
          {selectedCoach ? (
            <CoachStream
              key={previewRunKey}
              entries={previewEntries}
              name={coachName}
              slogan={coachSlogan}
              avatarSrc={avatarSrc}
              avatarText={avatarText}
              speed={previewSpeed}
              instant={isInstant}
              autoScroll={false}
              sessionKey={`overview-preview::${previewRunKey}`}
              className="dv-br-coachChooser__stream"
            />
          ) : (
            <div className="dv-br-coachChooser__empty">No coaches available.</div>
          )}
        </div>
      </div>

      <div className="dv-br-coachChooser__controls">
        <div className="dv-br-coachChooser__control">
          <span className="dv-br-coachChooser__label">Typing speed</span>

          <div className="dv-br-coachChooser__speedPills" role="group" aria-label="Typing speed">
            <button
              type="button"
              className={`dv-br-coachChooser__speedPill ${selectedTypingMode === 'instant' ? 'is-active' : ''}`}
              onClick={() => onTypingModeClick('instant')}
              disabled={isBusy}
              title="Instant text, no typing animation"
            >
              Instant
            </button>

            <button
              type="button"
              className={`dv-br-coachChooser__speedPill ${selectedTypingMode === 'fast' ? 'is-active' : ''}`}
              onClick={() => onTypingModeClick('fast')}
              disabled={isBusy}
              title="Bot typing speed"
            >
              Fast
            </button>

            <button
              type="button"
              className={`dv-br-coachChooser__speedPill ${selectedTypingMode === 'normal' ? 'is-active' : ''}`}
              onClick={() => onTypingModeClick('normal')}
              disabled={isBusy}
              title="Human typing speed"
            >
              Normal
            </button>
          </div>
        </div>
      </div>
    </section>
  );
}
